


import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  TextField,
  CircularProgress,
  Stack,
  Typography,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  Button,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import axios from "axios";
import { MdInbox, MdArrowForward, MdEdit, MdImage, MdCheckCircle, MdCancel, MdDelete } from "react-icons/md";

const Subcategories = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [editRowId, setEditRowId] = useState(null);
  const [editedImage, setEditedImage] = useState("");
  const [statusEditRowId, setStatusEditRowId] = useState(null);
  const [editedStatus, setEditedStatus] = useState(null);
  const [categoryCount, setCategoryCount] = useState(0);
  const [uploadingRowId, setUploadingRowId] = useState(null);

  const S3_UPLOAD_URL = "https://api.medstown.com/api/medshubs/upload-s3";

  // Debounced fetch
  const fetchData = useCallback(
    debounce(async (page, query) => {
      setLoading(true);
      try {
        const url = `https://api.medstown.com/api/medshubs/categories?upcat=${id}&page=${page}&limit=10${
          query ? `&search=${encodeURIComponent(query)}` : ""
        }`;
        const res = await axios.get(url);
        const respData = res.data?.data;
        setData(respData?.list || []);
        setTotalPages(res.data?.data?.totalPages || 1);
        setCategoryCount(res.data?.data?.totalCount || 0);
      } catch (error) {
        console.error("Error fetching subcategories:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [id]
  );

  useEffect(() => {
    fetchData(currentPage, searchQuery);
    // Cleanup debounce on unmount
    return () => fetchData.cancel();
  }, [currentPage, searchQuery, fetchData]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  // Image Editing Handlers
  const startImageEdit = (cid, currentImage) => {
    setEditRowId(cid);
    setEditedImage(currentImage || "");
    setStatusEditRowId(null);
  };

  const cancelImageEdit = () => {
    setEditRowId(null);
    setEditedImage("");
  };

  const saveImageEdit = async (row) => {
    if (!editedImage || editedImage.length > 300) {
      setSnackbar({ open: true, message: "Image URL is invalid or too long.", severity: "error" });
      return;
    }
    try {
      await axios.put(`https://api.medstown.com/api/medshubs/category/${row._id}`, {
        image_url: editedImage,
      });
      const updatedData = data.map((item) =>
        item.cid === row.cid ? { ...item, image_url: editedImage } : item
      );
      setData(updatedData);
      setSnackbar({ open: true, message: "Image updated successfully!", severity: "success" });
      setEditRowId(null);
      setEditedImage("");
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to update image.", severity: "error" });
    }
  };

  // Upload an image file to AWS S3 then persist its URL on the category
  const uploadImageToS3 = async (row, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setSnackbar({ open: true, message: "Please select an image file.", severity: "error" });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setSnackbar({ open: true, message: "Image must be less than 10MB.", severity: "error" });
      return;
    }
    setUploadingRowId(row.cid);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", "categories");
      const uploadRes = await axios.post(S3_UPLOAD_URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploadedUrl = uploadRes.data?.url;
      if (!uploadedUrl) throw new Error("No URL returned from upload");

      await axios.put(`https://api.medstown.com/api/medshubs/category/${row._id}`, {
        image_url: uploadedUrl,
      });

      setData((prev) =>
        prev.map((item) => (item.cid === row.cid ? { ...item, image_url: uploadedUrl } : item))
      );
      setEditedImage(uploadedUrl);
      setSnackbar({ open: true, message: "Image uploaded successfully!", severity: "success" });
      setEditRowId(null);
    } catch (err) {
      console.error("Image upload failed:", err);
      setSnackbar({ open: true, message: "Failed to upload image.", severity: "error" });
    } finally {
      setUploadingRowId(null);
    }
  };

  // Remove / clear the category image
  const removeImage = async (row) => {
    if (!window.confirm("Remove the image for this category?")) return;
    try {
      await axios.put(`https://api.medstown.com/api/medshubs/category/${row._id}`, {
        image_url: "",
      });
      setData((prev) =>
        prev.map((item) => (item.cid === row.cid ? { ...item, image_url: "" } : item))
      );
      setEditedImage("");
      setSnackbar({ open: true, message: "Image removed successfully!", severity: "success" });
      setEditRowId(null);
    } catch (err) {
      console.error("Failed to remove image:", err);
      setSnackbar({ open: true, message: "Failed to remove image.", severity: "error" });
    }
  };

  // Status Editing Handlers
  const startStatusEdit = (cid, currentStatus) => {
    setStatusEditRowId(cid);
    setEditedStatus(currentStatus);
    setEditRowId(null);
  };

  const cancelStatusEdit = () => {
    setStatusEditRowId(null);
    setEditedStatus(null);
  };

  const saveStatusEdit = async (row) => {
    try {
      await axios.put(`https://api.medstown.com/api/medshubs/category/${row._id}`, {
        status: editedStatus,
      });
      const updatedData = data.map((item) =>
        item.cid === row.cid ? { ...item, status: editedStatus } : item
      );
      setData(updatedData);
      setSnackbar({ open: true, message: "Status updated successfully!", severity: "success" });
      setStatusEditRowId(null);
      setEditedStatus(null);
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to update status.", severity: "error" });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // On row click navigate to subcategories
  const onRowClick = (cid) => {
    navigate(`/admin/sub-categories/${cid}`);
  };

  // Custom empty state for desktop/tablet
  const EmptyState = () => (
    <Stack spacing={1} alignItems="center" justifyContent="center" sx={{ py: 8 }}>
      <MdInbox size={48} color={theme.palette.grey[400]} />
      <Typography>No data found</Typography>
      <Typography variant="caption" color="text.secondary">
        No subcategories match your search criteria or the category is empty.
      </Typography>
    </Stack>
  );

  return (
    <Card sx={{ mt: 4, pb: 2 }}>
      {/* Header with Search and Count */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        px={2}
        pt={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
      >
        <TextField
          variant="outlined"
          size={isMobile ? "small" : "medium"}
          placeholder={isMobile ? "Search subcategories..." : "Search by Id or Name"}
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <Box ml={1} display="flex" alignItems="center" color={alpha(theme.palette.text.primary, 0.5)}>
                <MdInbox size={20} />
              </Box>
            ),
            endAdornment: searchQuery && (
              <IconButton size="small" onClick={clearSearch} aria-label="clear search">
                <MdArrowForward />
              </IconButton>
            ),
          }}
          sx={{
            width: { xs: "100%", sm: 300 },
            mb: { xs: 1, sm: 0 },
          }}
        />
        <Typography variant="body2" color="textSecondary" fontWeight={600}>
          Total Subcategories: {categoryCount}
        </Typography>
      </Stack>

      {/* Table or List for Desktop/Mobile */}
      {isMobile ? (
        // Mobile List View
        <>
          {loading && data.length === 0 ? (
            <Box textAlign="center" py={6}>
              <CircularProgress size={28} />
              <Typography variant="caption" sx={{ mt: 1 }}>
                Loading subcategories...
              </Typography>
            </Box>
          ) : data.length === 0 ? (
            <EmptyState />
          ) : (
            <List disablePadding>
              {data.map((row) => (
                <Box key={row.cid}>
                  <ListItem
                    button
                    onClick={() => onRowClick(row.cid)}
                    sx={{
                      px: 2,
                      py: 1,
                      "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        variant="rounded"
                        src={row.image_url || undefined}
                        alt={row.name}
                        sx={{ width: 56, height: 56, bgcolor: alpha(theme.palette.primary.main, 0.2) }}
                      >
                        <MdImage size={32} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography noWrap fontWeight={600}>
                          {row.name}
                        </Typography>
                      }
                      secondary={`ID: ${row.cid}`}
                      secondaryTypographyProps={{ noWrap: true, color: "text.secondary" }}
                      sx={{ ml: 2 }}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Edit Image">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditRowId(row.cid);
                            setEditedImage(row.image_url || "");
                            setStatusEditRowId(null);
                          }}
                          sx={{ mr: 1 }}
                          color="primary"
                        >
                          <MdEdit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Status">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setStatusEditRowId(row.cid);
                            setEditedStatus(row.status);
                            setEditRowId(null);
                          }}
                          color="secondary"
                        >
                          {row.status ? <MdCheckCircle color="green" /> : <MdCancel color="red" />}
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>

                  {/* Edit Image Inline */}
                  {editRowId === row.cid && (
                    <Box px={2} pb={2}>
                      {(editedImage || row.image_url) && (
                        <Box
                          component="img"
                          src={editedImage || row.image_url}
                          alt={row.name}
                          sx={{ width: 64, height: 64, objectFit: "cover", borderRadius: 1, mb: 1, border: `1px solid ${alpha(theme.palette.divider, 0.3)}` }}
                        />
                      )}
                      <Button
                        component="label"
                        size="small"
                        variant="outlined"
                        fullWidth
                        disabled={uploadingRowId === row.cid}
                        startIcon={
                          uploadingRowId === row.cid ? <CircularProgress size={14} /> : <MdImage />
                        }
                        sx={{ mb: 1 }}
                      >
                        {uploadingRowId === row.cid ? "Uploading..." : "Upload Image"}
                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            uploadImageToS3(row, file);
                          }}
                        />
                      </Button>
                      <TextField
                        label="Or paste Image URL"
                        variant="outlined"
                        size="small"
                        value={editedImage}
                        onChange={(e) => setEditedImage(e.target.value)}
                        fullWidth
                      />
                      <Stack direction="row" spacing={1} mt={1} justifyContent="space-between">
                        <Button
                          size="small"
                          color="error"
                          startIcon={<MdDelete />}
                          disabled={uploadingRowId === row.cid || !(editedImage || row.image_url)}
                          onClick={() => removeImage(row)}
                        >
                          Remove
                        </Button>
                        <Stack direction="row" spacing={1}>
                          <Button size="small" onClick={cancelImageEdit}>
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => saveImageEdit(row)}
                            disabled={!editedImage.trim() || uploadingRowId === row.cid}
                          >
                            Save URL
                          </Button>
                        </Stack>
                      </Stack>
                      <Divider sx={{ mt: 2 }} />
                    </Box>
                  )}

                  {/* Edit Status Inline */}
                  {statusEditRowId === row.cid && (
                    <Box px={2} pb={2}>
                      <Typography variant="body2" mb={1}>
                        Select status:
                      </Typography>
                      <Select
                        value={editedStatus ?? false}
                        onChange={(e) => setEditedStatus(e.target.value === "true" || e.target.value === true)}
                        fullWidth
                        size="small"
                      >
                        <MenuItem value={true}>Active</MenuItem>
                        <MenuItem value={false}>Inactive</MenuItem>
                      </Select>
                      <Stack direction="row" spacing={1} mt={1} justifyContent="flex-end">
                        <Button size="small" onClick={cancelStatusEdit}>
                          Cancel
                        </Button>
                        <Button size="small" variant="contained" onClick={() => saveStatusEdit(row)}>
                          Save
                        </Button>
                      </Stack>
                      <Divider sx={{ mt: 2 }} />
                    </Box>
                  )}

                  <Divider component="li" />
                </Box>
              ))}
            </List>
          )}
        </>
      ) : (
        // Desktop/Table View
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeaderStyle}>Category ID</TableCell>
                  <TableCell sx={tableHeaderStyle}>Category Name</TableCell>
                  <TableCell sx={tableHeaderStyle} align="center">Icon</TableCell>
                  <TableCell sx={tableHeaderStyle} align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={32} />
                      <Typography mt={1} fontSize="0.75rem" color="text.secondary">
                        Loading subcategories...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                      <Stack spacing={1} alignItems="center" justifyContent="center">
                        <MdInbox size={48} color={theme.palette.grey[400]} />
                        <Typography>No data found</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {searchQuery ? "No matching subcategories found." : "No subcategories available."}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row) => (
                    <TableRow 
                      key={row.cid} 
                      hover 
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          transform: "translateY(-1px)",
                          boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
                          transition: "all 0.2s ease",
                        },
                      }}
                      onClick={() => onRowClick(row.cid)}
                    >
                      <TableCell sx={tableCellStyle}>{row.cid}</TableCell>
                      <TableCell sx={tableCellStyle}>{row.name}</TableCell>
                      <TableCell sx={{ ...tableCellStyle, textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                        {editRowId === row.cid ? (
                          <Stack spacing={1} alignItems="center" justifyContent="center" sx={{ maxWidth: 200, mx: "auto" }}>
                            {(editedImage || row.image_url) && (
                              <Box
                                component="img"
                                src={editedImage || row.image_url}
                                alt={row.name}
                                sx={{ width: 60, height: 60, objectFit: "cover", borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.3)}` }}
                              />
                            )}
                            <Button
                              component="label"
                              size="small"
                              variant="outlined"
                              fullWidth
                              disabled={uploadingRowId === row.cid}
                              startIcon={
                                uploadingRowId === row.cid ? <CircularProgress size={14} /> : <MdImage />
                              }
                            >
                              {uploadingRowId === row.cid ? "Uploading..." : "Upload Image"}
                              <input
                                hidden
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  e.target.value = "";
                                  uploadImageToS3(row, file);
                                }}
                              />
                            </Button>
                            <TextField
                              label="Or paste Image URL"
                              variant="outlined"
                              size="small"
                              value={editedImage}
                              onChange={(e) => setEditedImage(e.target.value)}
                              fullWidth
                              sx={{ maxWidth: 180 }}
                            />
                            <Button
                              size="small"
                              color="error"
                              startIcon={<MdDelete />}
                              fullWidth
                              disabled={uploadingRowId === row.cid || !(editedImage || row.image_url)}
                              onClick={() => removeImage(row)}
                            >
                              Remove Image
                            </Button>
                            <Stack direction="row" spacing={1} justifyContent="flex-end" width="100%">
                              <Button size="small" onClick={cancelImageEdit}>Cancel</Button>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={!editedImage.trim() || uploadingRowId === row.cid}
                                onClick={() => saveImageEdit(row)}
                              >
                                Save URL
                              </Button>
                            </Stack>
                          </Stack>
                        ) : (
                          <Tooltip title="Click to edit image">
                            <Box
                              component="img"
                              src={row.image_url || "https://via.placeholder.com/60x60?text=No+Image"}
                              alt={row.name}
                              sx={{
                                width: 60,
                                height: 60,
                                objectFit: "cover",
                                borderRadius: 1,
                                cursor: "pointer",
                                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                startImageEdit(row.cid, row.image_url);
                              }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...tableCellStyle, textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                        {statusEditRowId === row.cid ? (
                          <Stack spacing={1} alignItems="center">
                            <Select
                              value={editedStatus ?? false}
                              onChange={(e) => setEditedStatus(e.target.value === "true" || e.target.value === true)}
                              size="small"
                              sx={{ width: 100 }}
                            >
                              <MenuItem value={true}>Active</MenuItem>
                              <MenuItem value={false}>Inactive</MenuItem>
                            </Select>
                            <Stack direction="row" spacing={1}>
                              <Button size="small" onClick={cancelStatusEdit}>Cancel</Button>
                              <Button size="small" variant="contained" onClick={() => saveStatusEdit(row)}>Save</Button>
                            </Stack>
                          </Stack>
                        ) : (
                          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                            <Typography sx={{ color: row.status ? "green" : "red", fontWeight: 600 }}>
                              {row.status ? "Active" : "Inactive"}
                            </Typography>
                            <Tooltip title="Click to edit status">
                              <IconButton size="small" onClick={() => startStatusEdit(row.cid, row.status)}>
                                <MdEdit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Pagination */}
          {totalPages > 1 && (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center" 
              py={1.5}
              px={{ xs: 1, sm: 2 }}
            >
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                size={isMobile ? "small" : "medium"}
                sx={{
                  "& .MuiPaginationItem-root": {
                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                    minWidth: { xs: 30, sm: 36 },
                    height: { xs: 30, sm: 36 },
                    margin: "0 4px",
                  },
                  "& .MuiPaginationItem-page": {
                    padding: "6px 10px",
                  },
                  "& .Mui-selected": {
                    backgroundColor: `${theme.palette.primary.main} !important`,
                    color: "#fff",
                  },
                }}
              />
            </Box>
          )}
        </>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

const tableHeaderStyle = {
  fontWeight: 700,
  color: "primary.main",
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  backgroundColor: "rgba(1, 77, 77, 0.05)",
  py: 1,
  px: 2,
};

const tableCellStyle = {
  fontSize: 13,
  py: 1,
  px: 2,
  maxWidth: 200,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export default Subcategories;
